import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { reloadPage } from '@/utils/reloadPage';

/**
 * Session key recording the last time a failed dynamic import forced a reload,
 * so we reload at most once within a short window. Without this guard a
 * genuinely broken/removed chunk would reload forever.
 */
const RELOAD_MARKER = 'lazy-retry:last-reload-at';
const RELOAD_WINDOW_MS = 10_000;

/**
 * Drop-in replacement for React.lazy that self-heals a failed dynamic import.
 *
 * A code-split chunk can fail to load for two benign, transient reasons:
 *   - In production, a new deploy replaced the hashed chunk files (rsync
 *     `--delete`) while the tab still holds the previous index.html, so the old
 *     chunk URL now 404s ("Failed to fetch dynamically imported module").
 *   - In dev, Vite re-optimized its dependency pre-bundle and the already-open
 *     page requests a now-stale `?v=…` URL ("504 Outdated Optimize Dep").
 *
 * In both cases a single full-page reload pulls the fresh index.html + current
 * chunks and the navigation succeeds. We only reload once per short window
 * (tracked in sessionStorage); a second failure bubbles to the route error
 * boundary so a truly broken import is still surfaced instead of looping.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_MARKER) ?? '0');
      const now = Date.now();

      if (now - lastReloadAt > RELOAD_WINDOW_MS) {
        window.sessionStorage.setItem(RELOAD_MARKER, String(now));
        reloadPage();
        // Never resolve — the reload is about to replace the page, so nothing
        // should render (or throw) in the meantime.
        return new Promise<{ default: T }>(() => {});
      }

      throw error;
    }
  });
}
